import React, { useState } from 'react';

const MSMEVerificationDashboard = ({ extractedData, onVerify }) => {
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedUan, setVerifiedUan] = useState('');

  const handleVerify = async () => {
    const currentUan = extractedData?.registration_number;
    if (isVerifying || !currentUan || verifiedUan === currentUan) return;
    
    setIsVerifying(true);
    try {
      const result = await onVerify(extractedData);
      setVerificationResult(result);
      setVerifiedUan(currentUan);
    } catch (error) {
      setVerificationResult({
        status: 'error',
        message: 'Verification failed'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'match':
        return <span className="text-green-500">✅</span>;
      case 'partial':
        return <span className="text-yellow-500">⚠️</span>;
      case 'mismatch':
        return <span className="text-red-500">❌</span>;
      default:
        return <span className="text-gray-500">-</span>;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'match':
        return 'bg-green-50 border-green-200';
      case 'partial':
        return 'bg-yellow-50 border-yellow-200';
      case 'mismatch':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  if (!extractedData?.registration_number) {
    return null;
  }

  if (extractedData.registration_number !== verifiedUan && verificationResult) {
    setVerificationResult(null);
    setVerifiedUan('');
  }

  const isAlreadyVerified = verifiedUan === extractedData.registration_number && verificationResult;

  return (
    <div className="mt-6 p-4 border rounded-lg bg-blue-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-blue-800">MSME (Udyog Aadhaar) Verification</h3>
        <button
          onClick={handleVerify}
          disabled={isVerifying || isAlreadyVerified}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {isVerifying ? 'Verifying...' : isAlreadyVerified ? 'Verified' : 'Verify with Official API'}
        </button>
      </div>

      {verificationResult && (
        <div className="space-y-4">
          {verificationResult.status === 'error' ? (
            <div className="p-3 bg-red-100 border border-red-300 rounded text-red-700">
              {verificationResult.message}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3">
                {Object.entries(verificationResult.verification_results || {}).map(([field, result]) => (
                  <div key={field} className={`p-3 border rounded ${getStatusColor(result.status)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium capitalize">{field.replace(/_/g, ' ')}</span>
                      {getStatusIcon(result.status)}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Extracted:</span>
                        <div className="font-mono">{result.extracted || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Official API:</span>
                        <div className="font-mono">{result.api || 'N/A'}</div>
                      </div>
                    </div>
                    {result.similarity && (
                      <div className="mt-1 text-xs text-gray-600">
                        Similarity: {(result.similarity * 100).toFixed(1)}%
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-gray-100 border rounded">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Overall Verification Score</span>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                    verificationResult.overall_match?.status === 'verified' 
                      ? 'bg-green-100 text-green-800'
                      : verificationResult.overall_match?.status === 'partial'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {verificationResult.overall_match?.score}% 
                    ({verificationResult.overall_match?.status})
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {verificationResult.overall_match?.matched_fields} of {verificationResult.overall_match?.total_fields} fields matched
                </div>
              </div>

              {verificationResult.api_data?.main_details && (
                <div className="mt-4 bg-white p-4 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">Additional Details from API</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {verificationResult.api_data.main_details.organization_type && (
                      <div className="text-xs">
                        <span className="text-gray-600">Organization Type:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.organization_type}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.social_category && (
                      <div className="text-xs">
                        <span className="text-gray-600">Social Category:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.social_category}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.gender && (
                      <div className="text-xs">
                        <span className="text-gray-600">Gender:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.gender}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.pin && (
                      <div className="text-xs">
                        <span className="text-gray-600">PIN Code:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.pin}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.mobile_number && (
                      <div className="text-xs">
                        <span className="text-gray-600">Mobile:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.mobile_number}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.email && (
                      <div className="text-xs">
                        <span className="text-gray-600">Email:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.email}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.flat && (
                      <div className="text-xs">
                        <span className="text-gray-600">Flat/Door No:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.flat}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.name_of_building && (
                      <div className="text-xs">
                        <span className="text-gray-600">Building:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.name_of_building}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.road && (
                      <div className="text-xs">
                        <span className="text-gray-600">Road:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.road}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.village && (
                      <div className="text-xs">
                        <span className="text-gray-600">Village:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.village}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.block && (
                      <div className="text-xs">
                        <span className="text-gray-600">Block:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.block}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.city && (
                      <div className="text-xs">
                        <span className="text-gray-600">City:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.city}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.state && (
                      <div className="text-xs">
                        <span className="text-gray-600">State:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.state}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.dic_name && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">District Industries Centre:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.dic_name}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.msme_dfo && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">MSME DFO:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.msme_dfo}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.date_of_incorporation && (
                      <div className="text-xs">
                        <span className="text-gray-600">Date of Incorporation:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.date_of_incorporation}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.applied_date && (
                      <div className="text-xs">
                        <span className="text-gray-600">Applied Date:</span>
                        <div className="font-medium text-blue-900 mt-1">{verificationResult.api_data.main_details.applied_date}</div>
                      </div>
                    )}
                    {verificationResult.api_data.main_details.enterprise_type_list && verificationResult.api_data.main_details.enterprise_type_list.length > 0 && (
                      <div className="text-xs col-span-full">
                        <span className="text-gray-600">Enterprise Classification History:</span>
                        <div className="mt-1 space-y-1">
                          {verificationResult.api_data.main_details.enterprise_type_list.map((item, idx) => (
                            <div key={idx} className="font-medium text-blue-900">
                              • {item.classification_year}: {item.enterprise_type} (Classified on: {item.classification_date})
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {verificationResult.api_data?.location_of_plant_details && verificationResult.api_data.location_of_plant_details.length > 0 && (
                <div className="mt-4 bg-white p-4 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">Plant Locations ({verificationResult.api_data.location_of_plant_details.length})</h4>
                  <div className="space-y-3">
                    {verificationResult.api_data.location_of_plant_details.map((location, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded border">
                        <div className="font-medium text-blue-900 mb-2">{location.unit_name}</div>
                        <div className="text-xs text-gray-700">
                          {[location.line_1, location.building, location.road, location.city, location.district, location.state, location.pin]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {verificationResult.api_data?.nic_code && verificationResult.api_data.nic_code.length > 0 && (
                <div className="mt-4 bg-white p-4 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">NIC Codes ({verificationResult.api_data.nic_code.length})</h4>
                  <div className="space-y-2">
                    {verificationResult.api_data.nic_code.map((nic, idx) => (
                      <div key={idx} className="p-2 bg-gray-50 rounded text-xs">
                        <div className="font-medium text-blue-900">{nic.nic_5_digit}</div>
                        <div className="text-gray-600 mt-1">Activity: {nic.activity_type}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MSMEVerificationDashboard;
